import { useQuery } from "@tanstack/react-query";
import type { SeriesItem, SeriesSeason } from "@hybrid-iptv/types";
import { cleanTitle } from "@hybrid-iptv/tmdb-client";
import { api } from "../api/client.js";
import { nameKey, tvmazeQueryFor, withoutDisambiguator, type TvMazeHit } from "../home/historyArtwork.js";

/**
 * What a provider's panel leaves out of a series' episodes, from TVmaze (owner, 2026-10-12:
 * "Lanterns isn't showing any episode information besides title name"). A panel sends each
 * episode's file, still, air date and length, and almost never its plot: across eight series
 * on the onn box, one had plots (Slow Horses, 5 of 6); The Rookie 0 of 20, Ted Lasso 0 of 10,
 * Lanterns 0 of 8. A few panels also send a placeholder for the name ("Lanterns - S01E02").
 *
 * TVmaze (free, no key; relayed by /api/tv/search and /api/tv/episodes) has the real name,
 * the plot, the air date, the length and a picture for each. They fill the gaps only: whatever
 * the panel sent is kept, and an episode TVmaze lists that the panel does not have is not
 * added, because there is nothing to play.
 */

/** One episode of TVmaze's /shows/:id/episodes. */
export interface TvMazeEpisode {
  season?: number;
  number?: number | null;
  name?: string | null;
  airdate?: string | null;
  runtime?: number | null;
  summary?: string | null;
  image?: { medium?: string | null; original?: string | null } | null;
}

/** "<p>Hal <b>Jordan</b> &amp; John Stewart.</p>" → "Hal Jordan & John Stewart." */
export function plainSummary(html: string | null | undefined): string | undefined {
  if (!html) return undefined;
  const text = html
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;|&apos;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 0 ? text : undefined;
}

/** The TVmaze show a series' title names: equal name (a "(2005)" or "(US)" ignored), the nearest premiere year. */
export function pickTvmazeShow(hits: readonly TvMazeHit[] | undefined, title: string, year?: number): number | undefined {
  const wanted = nameKey(title);
  if (wanted.length < 2) return undefined;
  const named = (hits ?? []).filter((hit) => {
    const name = hit.show?.name;
    return !!name && typeof hit.show?.id === "number" && nameKey(withoutDisambiguator(name)) === wanted;
  });
  if (named.length === 0) return undefined;
  if (!year) return named[0]!.show!.id;
  const premiere = (hit: TvMazeHit) => Number(hit.show?.premiered?.slice(0, 4)) || undefined;
  let best = named[0]!;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const hit of named) {
    const at = premiere(hit);
    const gap = at === undefined ? 1000 : Math.abs(at - year);
    if (gap < bestGap) {
      best = hit;
      bestGap = gap;
    }
  }
  return best.show!.id;
}

/** A panel's stand-in for an episode's name: "Episode 4", "E04", or "<Show> - S01E02". */
export function isPlaceholderTitle(title: string): boolean {
  const text = title.trim();
  return text === "" || /^(episode|ep\.?|e)\s*[0-9]+$/i.test(text) || /(^|[^a-z0-9])s[0-9]{1,3}\s*e[0-9]{1,3}([^a-z0-9]|$)/i.test(text);
}

const https = (url: string | null | undefined) => (url ? url.replace(/^http:\/\//i, "https://") : undefined);

/**
 * The panel's seasons with TVmaze's facts filled into their gaps, matched by season and
 * episode number. Returns the same array when nothing changed, so memos downstream stay put.
 *
 * An episode's picture: the panel's is kept when it can be that episode's own. It is replaced by
 * TVmaze's picture of the episode when the panel sent none, sent one it also gave another episode
 * of the series (seen 2026-10-12: Carrie, 8 episodes with 4 pictures), or sent the series' own
 * poster or backdrop (`seriesPictures`).
 */
export function mergeTvmazeEpisodes(
  seasons: readonly SeriesSeason[],
  episodes: readonly TvMazeEpisode[],
  seriesPictures: ReadonlyArray<string | undefined> = [],
): SeriesSeason[] {
  const byKey = new Map<string, TvMazeEpisode>();
  for (const episode of episodes) {
    if (typeof episode.season === "number" && typeof episode.number === "number") byKey.set(`${episode.season}x${episode.number}`, episode);
  }
  // A picture used by more than one episode, or the series' own, is not an episode's picture.
  const uses = new Map<string, number>();
  for (const season of seasons) for (const episode of season.episodes) if (episode.stillUrl) uses.set(episode.stillUrl, (uses.get(episode.stillUrl) ?? 0) + 1);
  const notAnEpisodes = new Set(seriesPictures.filter((url): url is string => !!url));
  let changed = false;
  const merged = seasons.map((season) => {
    let seasonChanged = false;
    const next = season.episodes.map((episode) => {
      const fact = byKey.get(`${episode.seasonNumber}x${episode.episodeNumber}`);
      if (!fact) return episode;
      const fill: Partial<typeof episode> = {};
      const plot = plainSummary(fact.summary);
      if (!episode.overview && plot) fill.overview = plot;
      if (fact.name && fact.name.trim() && isPlaceholderTitle(episode.title)) fill.title = fact.name.trim();
      if (!episode.airDate && fact.airdate && /^[0-9]{4}-[0-9]{2}-[0-9]{2}/.test(fact.airdate)) fill.airDate = fact.airdate.slice(0, 10);
      if (!episode.runtimeMinutes && typeof fact.runtime === "number" && fact.runtime > 0) fill.runtimeMinutes = fact.runtime;
      const still = https(fact.image?.medium ?? fact.image?.original);
      const sharedStill = !!episode.stillUrl && ((uses.get(episode.stillUrl) ?? 0) > 1 || notAnEpisodes.has(episode.stillUrl));
      if (still && (!episode.stillUrl || sharedStill) && still !== episode.stillUrl) fill.stillUrl = still;
      if (Object.keys(fill).length === 0) return episode;
      seasonChanged = true;
      return { ...episode, ...fill };
    });
    if (!seasonChanged) return season;
    changed = true;
    return { ...season, episodes: next };
  });
  return changed ? merged : (seasons as SeriesSeason[]);
}

/**
 * A series' episode facts from TVmaze: the show is found by its cleaned title (and the year in
 * it, when there is one), then its episode list is fetched. Nothing is asked until the panel's
 * own seasons have arrived and have an episode without a plot or a real name.
 */
export function useTvmazeEpisodes(series: SeriesItem | null | undefined, enabled: boolean) {
  const cleaned = series ? cleanTitle(series.rawTitle) : undefined;
  const title = cleaned?.title ?? "";
  const year = cleaned?.year;
  const show = useQuery({
    queryKey: ["tvmaze", "show", title.toLowerCase(), year ?? null],
    queryFn: async (): Promise<number | null> => {
      const hits = await api.get<TvMazeHit[]>(`/api/tv/search?q=${encodeURIComponent(tvmazeQueryFor(title))}`);
      return pickTvmazeShow(hits, title, year) ?? null;
    },
    enabled: enabled && title.length >= 2,
    staleTime: 1000 * 60 * 60 * 24,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const showId = show.data ?? undefined;
  return useQuery({
    queryKey: ["tvmaze", "episodes", showId ?? null],
    queryFn: () => api.get<TvMazeEpisode[]>(`/api/tv/episodes?show=${showId}`),
    enabled: enabled && showId !== undefined,
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
